import type { SupabaseClient } from "@supabase/supabase-js";

export type PlatformSubscriptionStatus =
  | "unmanaged"
  | "active"
  | "suspended"
  | "cancelled"
  | "expired";

export interface PlatformProfile {
  isPlatformOwner: boolean;
  ownerName?: string;
}

export interface PlatformSubscriptionRow {
  farmId: string;
  farmName: string;
  ownerUserId?: string;
  ownerName: string;
  subscriptionId?: string;
  subscriptionCode?: string;
  planCode: string;
  status: PlatformSubscriptionStatus;
  startsOn?: string;
  expiresOn?: string;
  daysRemaining?: number;
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function rows(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map(record) : [];
}

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function status(value: unknown): PlatformSubscriptionStatus {
  if (
    value === "unmanaged" ||
    value === "active" ||
    value === "suspended" ||
    value === "cancelled" ||
    value === "expired"
  ) {
    return value;
  }
  throw new Error("PLATFORM_SUBSCRIPTION_STATUS_INVALID");
}

export async function loadPlatformProfile(client: SupabaseClient): Promise<PlatformProfile> {
  const { data, error } = await client.rpc("platform_profile_v1");
  if (error) throw error;
  const row = rows(data)[0] ?? {};
  const ownerName = optionalString(row.owner_name);
  return {
    isPlatformOwner: row.is_platform_owner === true,
    ...(ownerName ? { ownerName } : {}),
  };
}

export async function loadPlatformSubscriptions(
  client: SupabaseClient,
): Promise<PlatformSubscriptionRow[]> {
  const { data, error } = await client.rpc("platform_subscription_dashboard_v1");
  if (error) throw error;

  return rows(data).map((row) => {
    const ownerUserId = optionalString(row.owner_user_id);
    const subscriptionId = optionalString(row.subscription_id);
    const subscriptionCode = optionalString(row.subscription_code);
    const startsOn = optionalString(row.starts_on);
    const expiresOn = optionalString(row.expires_on);
    const days =
      typeof row.days_remaining === "number"
        ? row.days_remaining
        : typeof row.days_remaining === "string" && row.days_remaining.trim()
          ? Number(row.days_remaining)
          : undefined;

    return {
      farmId: String(row.farm_id),
      farmName: String(row.farm_name ?? "سالن"),
      ...(ownerUserId ? { ownerUserId } : {}),
      ownerName: String(row.owner_name ?? "بدون مالک فعال"),
      ...(subscriptionId ? { subscriptionId } : {}),
      ...(subscriptionCode ? { subscriptionCode } : {}),
      planCode: String(row.plan_code ?? "standard"),
      status: status(row.status),
      ...(startsOn ? { startsOn } : {}),
      ...(expiresOn ? { expiresOn } : {}),
      ...(typeof days === "number" && Number.isFinite(days) ? { daysRemaining: days } : {}),
    };
  });
}

export async function startPlatformSubscription(
  client: SupabaseClient,
  farmId: string,
  planCode: string,
  days: number,
): Promise<void> {
  const { error } = await client.rpc("platform_subscription_start_v1", {
    target_farm_id: farmId,
    target_plan_code: planCode,
    target_days: days,
  });
  if (error) throw error;
}

export async function updatePlatformSubscription(
  client: SupabaseClient,
  subscriptionId: string,
  action: "suspend" | "activate" | "renew",
  days?: number,
): Promise<void> {
  const { error } = await client.rpc("platform_subscription_action_v1", {
    target_subscription_id: subscriptionId,
    target_action: action,
    target_days: action === "renew" ? (days ?? null) : null,
  });
  if (error) throw error;
}
